import styled from '@emotion/styled';
import { motion } from 'framer-motion';

export const PageWrapper = styled.div<{ colors: any }>`
  background: transparent;
  position: relative;
  overflow-x: hidden;
`;

export const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 40px;
  text-align: center;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    max-width: 100%;
    padding: 0 20px;
    width: 100%;
  }
`;

export const Section = styled(motion.section)`
  padding: 80px 0;
  position: relative;
  width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
  margin: 0;
  
  scroll-margin-top: 80px;
  scroll-behavior: smooth;
  transition: opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1),
              transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  
  @media (max-width: 768px) {
    padding: 60px 0;
    width: 100%;
    overflow-x: hidden;
    margin: 0;
  }
`;

export const HeroSection = styled(Section)<{ isScrolled: boolean }>`
  /* Gleiche Höhe wie HeaderVideoContainer: Bild Aspect Ratio 3560x2478 = 1.436:1 */
  height: ${props => props.isScrolled ? '0' : 'calc(100vw / 1.436)'};
  min-height: ${props => props.isScrolled ? '0' : '100vh'};
  position: relative;
  padding: 0 !important;
  margin: 0 !important;
  opacity: ${props => props.isScrolled ? 0 : 1};
  transition: opacity 0.3s ease;
  pointer-events: ${props => props.isScrolled ? 'none' : 'auto'};
  overflow: hidden;
  
  @media (max-width: 1100px) {
    height: ${props => props.isScrolled ? '0' : 'calc(100vw / 1.436)'};
    min-height: ${props => props.isScrolled ? '0' : '100vh'};
    transition: none;
  }
`;

// Split Layout für Intro Section
export const SplitSection = styled(motion.section)`
  display: flex;
  min-height: 100vh;
  position: relative;
  margin: 0 !important;
  padding: 0 !important;
  z-index: 1;
  
  @media (max-width: 1100px) {
    flex-direction: column;
    min-height: auto;
    position: relative;
    margin: 0 !important;
    padding: 0 !important;
    pointer-events: auto !important;
    z-index: 1;
    background: transparent;
    opacity: 1 !important;
    
    > div:first-child {
      order: 1;
    }
    
    > div:last-child {
      order: 2;
    }
  }
`;

// Split Layout für andere Sections (ohne margin-top) - kommt NACH der Intro Section
export const SplitSectionNormal = styled(motion.section)`
  display: flex;
  height: auto;
  position: relative;
  margin-top: 0;
  z-index: 0;
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 1100px) {
    flex-direction: column;
    height: auto;
    position: relative;
    margin-top: 0;
    pointer-events: auto !important;
    z-index: 0;
    background: transparent;
    opacity: 1 !important;
    width: 100%;
    box-sizing: border-box;
  }
`;

export const SplitLeft = styled.div<{ bgColor: string; scrollProgress?: number; noPadding?: boolean }>`
  flex: 0 0 ${props => props.noPadding ? '40%' : '60%'};
  background: ${props => props.noPadding ? 'transparent' : props.bgColor};
  display: flex;
  align-items: ${props => props.noPadding ? 'stretch' : 'center'};
  justify-content: ${props => props.noPadding ? 'stretch' : 'center'};
  padding: ${props => props.noPadding ? '0' : '80px 60px'};
  min-width: 0;
  height: auto;
  min-height: fit-content;
  position: relative;
  overflow: ${props => props.noPadding ? 'hidden' : 'visible'};
  
  /* Bilder sollen den verfügbaren Platz ausfüllen */
  img.desktop-image,
  img.mobile-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
  }
  
  @media (max-width: 968px) {
    flex: 1;
    height: auto;
    min-height: auto;
    padding: ${props => props.noPadding ? '0' : '150px 40px 60px'};
    align-items: ${props => props.noPadding ? 'stretch' : 'flex-start'};
    background: ${props => props.noPadding ? 'transparent' : props.bgColor};
    position: relative;
    z-index: ${props => props.noPadding ? '1' : '2'};
    margin-top: 0;
    overflow: ${props => props.noPadding ? 'hidden' : 'visible'};
    order: ${props => props.noPadding ? '2' : '1'};
  }
  
  @media (max-width: 768px) {
    padding: ${props => props.noPadding ? '0' : '130px 30px 50px'};
  }
  
  @media (max-width: 480px) {
    padding: ${props => props.noPadding ? '0' : '120px 20px 40px'};
  }
`;

export const SplitRight = styled.div<{ scrollProgress?: number }>`
  flex: 0 0 40%;
  position: relative;
  overflow: hidden;
  height: auto;
  min-height: fit-content;
  align-self: stretch;
  
  /* Bilder sollen den verfügbaren Platz ausfüllen */
  img.desktop-image,
  img.mobile-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
  }
  
  @media (max-width: 968px) {
    flex: 1;
    position: relative;
    width: 100%;
    height: auto;
    min-height: auto;
    z-index: 1;
    overflow: hidden;
    display: flex;
    align-items: stretch;
    order: 2;
  }
`;

export const SplitRightWithBg = styled.div<{ scrollProgress?: number; bgColor?: string }>`
  flex: 0 0 60%;
  background: ${props => props.bgColor || 'transparent'};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 80px 60px;
  min-width: 0;
  height: auto;
  min-height: fit-content;
  position: relative;
  overflow: hidden;
  
  /* Bilder sollen den verfügbaren Platz ausfüllen */
  img.desktop-image,
  img.mobile-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
  }
  
  @media (max-width: 968px) {
    flex: 1;
    height: auto;
    min-height: auto;
    padding: 150px 40px 60px;
    align-items: flex-start;
    background: ${props => props.bgColor || 'transparent'};
    position: relative;
    z-index: 2;
    margin-top: 0;
    order: 1;
  }
  
  @media (max-width: 768px) {
    padding: 130px 30px 50px;
    
    /* Spezifisch für yoga-outdoor Sektion: weniger padding-top auf Mobile */
    &.yoga-outdoor-section {
      padding: 80px 30px 50px;
    }
  }
  
  @media (max-width: 480px) {
    padding: 120px 20px 40px;
    
    /* Spezifisch für yoga-outdoor Sektion: weniger padding-top auf Mobile */
    &.yoga-outdoor-section {
      padding: 60px 20px 40px;
    }
  }
`;

export const VideoContainer = styled(motion.div)`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin: clamp(10px, 1.5vw, 15px) 0;
  
  .lotus-media {
    max-width: 30%;
    height: auto;
    display: block;
    
    @media (max-width: 768px) {
      max-width: 35%;
    }
    
    @media (max-width: 480px) {
      max-width: 40%;
    }
  }
  
  video.lotus-media {
    image-rendering: auto;
    object-fit: contain;
  }
  
  img.lotus-media {
    image-rendering: auto;
  }
`;

export const SplitContent = styled.div<{ textColor: string }>`
  color: ${props => props.textColor};
  text-align: center;
  max-width: 800px;
  width: 100%;
  word-wrap: break-word;
  overflow-wrap: break-word;
  position: relative;
  z-index: 2;
  
  p {
    font-family: 'Poppins', sans-serif;
    font-size: clamp(0.85rem, 1.2vw, 1.1rem);
    line-height: clamp(1.4, 1.8vw, 1.6);
    margin-bottom: clamp(20px, 2.5vw, 30px);
    white-space: pre-line;
    word-break: keep-all;
    cursor: default;
    position: relative;
    
    /* Scroll-basierte Animation */
    @keyframes fadeInUp {
      from {
        opacity: 0;
        transform: translateY(30px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    
    /* Glow-Effekt beim Scrollen */
    &.in-view {
      animation: fadeInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1) forwards;
    }
    
    @media (max-width: 968px) {
      font-size: clamp(1.1rem, 2.5vw, 1.4rem);
      line-height: clamp(1.6, 2.5vw, 1.9);
      word-break: break-word;
    }
    
    @media (max-width: 480px) {
      font-size: clamp(1.2rem, 3vw, 1.5rem);
      line-height: clamp(1.7, 3vw, 2);
    }
  }
`;

// Einheitliche Text-Komponenten - Desktop bleibt gleich, nur Mobile wird angepasst
export const IntroText = styled(motion.p)`
  font-family: 'Poppins', sans-serif;
  font-size: clamp(0.85rem, 1.5vw, 1.2rem);
  line-height: clamp(1.4, 2vw, 1.7);
  
  @media (max-width: 968px) {
    font-size: clamp(1.3rem, 3vw, 1.6rem);
    line-height: clamp(1.8, 3vw, 2.1);
  }
  
  @media (max-width: 480px) {
    font-size: clamp(1.4rem, 4vw, 1.7rem);
    line-height: clamp(1.9, 4vw, 2.2);
  }
  margin-bottom: clamp(20px, 3vw, 30px);
  white-space: pre-line;
  transform-style: preserve-3d;
  perspective: 1000px;
  
  @media (max-width: 968px) {
    font-size: clamp(1.3rem, 3vw, 1.6rem);
    line-height: clamp(1.8, 3vw, 2.1);
  }
  
  @media (max-width: 480px) {
    font-size: clamp(1.4rem, 4vw, 1.7rem);
    line-height: clamp(1.9, 4vw, 2.2);
  }
`;

export const SectionTitle = styled(motion.h2)<{ textColor: string }>`
  font-family: 'Poppins', sans-serif;
  font-size: clamp(1.8rem, 2.5vw, 2.5rem);
  line-height: clamp(2.2, 3vw, 3);
  font-weight: 700;
  color: ${props => props.textColor};
  margin-bottom: clamp(25px, 3vw, 35px);
  transform-style: preserve-3d;
  perspective: 1000px;
  
  @media (max-width: 968px) {
    font-size: clamp(2rem, 4vw, 2.8rem);
    line-height: clamp(2.4, 4vw, 3.2);
  }
  
  @media (max-width: 480px) {
    font-size: clamp(2.2rem, 5vw, 3rem);
    line-height: clamp(2.6, 5vw, 3.4);
  }
`;

export const SectionText = styled(motion.p)<{ textColor: string }>`
  font-family: 'Poppins', sans-serif;
  font-size: clamp(0.85rem, 1.2vw, 1.1rem);
  line-height: clamp(1.4, 1.8vw, 1.6);
  color: ${props => props.textColor};
  margin-bottom: clamp(20px, 2.5vw, 30px);
  white-space: pre-line;
  transform-style: preserve-3d;
  perspective: 1000px;
  
  @media (max-width: 968px) {
    font-size: clamp(1.1rem, 2.5vw, 1.4rem);
    line-height: clamp(1.6, 2.5vw, 1.9);
  }
  
  @media (max-width: 480px) {
    font-size: clamp(1.2rem, 3vw, 1.5rem);
    line-height: clamp(1.7, 3vw, 2);
  }
`;

export const SectionHeading = styled(motion.h3)<{ textColor: string }>`
  font-family: 'Poppins', sans-serif;
  font-size: clamp(0.8rem, 1.2vw, 1rem);
  line-height: clamp(1.3, 1.8vw, 1.5);
  font-weight: 600;
  color: ${props => props.textColor};
  margin-bottom: clamp(20px, 3vw, 30px);
  transform-style: preserve-3d;
  perspective: 1000px;
  
  @media (max-width: 968px) {
    font-size: clamp(1.3rem, 3vw, 1.6rem);
    line-height: clamp(1.8, 3vw, 2.1);
  }
  
  @media (max-width: 480px) {
    font-size: clamp(1.4rem, 4vw, 1.7rem);
    line-height: clamp(1.9, 4vw, 2.2);
  }
`;

export const HighlightText = styled.span`
  font-weight: 700;
  color: inherit;
`;

export const CTAButton = styled(motion.button)<{ variant?: 'primary' | 'secondary'; colors?: any }>`
  font-family: 'Poppins', sans-serif;
  font-size: clamp(1rem, 1.2vw, 1.1rem);
  font-weight: 600;
  padding: clamp(12px, 1.5vw, 16px) clamp(24px, 3vw, 32px);
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: ${props => {
    if (props.variant === 'secondary') {
      return props.colors?.secondary || '#fff';
    }
    return props.colors?.primary || '#E6007A';
  }};
  color: ${props => {
    if (props.variant === 'secondary') {
      return props.colors?.primary || '#E6007A';
    }
    return '#fff';
  }};
  
  @media (max-width: 968px) {
    font-size: clamp(1.1rem, 2.5vw, 1.3rem);
    padding: clamp(14px, 2vw, 18px) clamp(28px, 3.5vw, 36px);
  }
  
  @media (max-width: 480px) {
    font-size: clamp(1.2rem, 3vw, 1.4rem);
    padding: clamp(16px, 2.5vw, 20px) clamp(32px, 4vw, 40px);
  }
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

export const ImageContainer = styled(motion.div)`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const SectionWithBg = styled(motion.section)<{ bgColor: string }>`
  background: ${props => props.bgColor};
  padding: clamp(60px, 8vw, 100px) 0;
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: clamp(50px, 6vw, 80px) 0;
  }
  
  @media (max-width: 480px) {
    padding: clamp(40px, 5vw, 70px) 0;
  }
`;

export const TestimonialMotto = styled.h2`
  font-family: 'Poppins', sans-serif;
  font-size: clamp(1.8rem, 2.5vw, 2.5rem);
  line-height: clamp(2.2, 3vw, 3);
  font-weight: 700;
  color: #333;
  margin-bottom: clamp(40px, 5vw, 60px);
  
  @media (max-width: 968px) {
    font-size: clamp(2rem, 4vw, 2.8rem);
    line-height: clamp(2.4, 4vw, 3.2);
  }
  
  @media (max-width: 480px) {
    font-size: clamp(2.2rem, 5vw, 3rem);
    line-height: clamp(2.6, 5vw, 3.4);
  }
`;

export const TestimonialMottoWrapper = styled(motion.div)`
  margin-bottom: clamp(40px, 5vw, 60px);
`;

export const TestimonialsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: clamp(20px, 3vw, 40px);
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 968px) {
    grid-template-columns: repeat(2, 1fr);
    gap: clamp(20px, 3vw, 30px);
    width: 100%;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr !important;
    gap: clamp(20px, 4vw, 30px);
    width: 100%;
    max-width: 100%;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr !important;
    gap: 20px;
    width: 100%;
    max-width: 100%;
  }
`;

export const TestimonialCard = styled(motion.div)`
  background: #fff;
  border-radius: 12px;
  padding: clamp(20px, 3vw, 30px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  transition: transform 0.3s ease;
  
  @media (max-width: 480px) {
    padding: clamp(24px, 4vw, 32px);
  }
`;

export const TestimonialAvatar = styled.img`
  width: clamp(60px, 8vw, 80px);
  height: clamp(60px, 8vw, 80px);
  border-radius: 50%;
  object-fit: cover;
  margin-bottom: clamp(15px, 2vw, 20px);
  
  @media (max-width: 480px) {
    width: clamp(70px, 10vw, 90px);
    height: clamp(70px, 10vw, 90px);
  }
`;

export const StarsContainer = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: clamp(15px, 2vw, 20px);
  font-size: clamp(18px, 2.5vw, 24px);
  
  @media (max-width: 480px) {
    font-size: clamp(20px, 3vw, 26px);
  }
`;

export const TestimonialText = styled.p`
  font-family: 'Poppins', sans-serif;
  font-size: clamp(0.7rem, 1.2vw, 1rem);
  line-height: clamp(1.3, 1.8vw, 1.5);
  color: #666;
  margin-bottom: clamp(15px, 2vw, 20px);
  font-style: italic;
  
  @media (max-width: 968px) {
    font-size: clamp(1.3rem, 3vw, 1.6rem);
    line-height: clamp(1.8, 3vw, 2.1);
  }
  
  @media (max-width: 480px) {
    font-size: clamp(1.4rem, 4vw, 1.7rem);
    line-height: clamp(1.9, 4vw, 2.2);
  }
`;

export const TestimonialAuthor = styled.p`
  font-family: 'Poppins', sans-serif;
  font-size: clamp(0.8rem, 1.2vw, 1rem);
  line-height: clamp(1.3, 1.8vw, 1.5);
  font-weight: 600;
  color: #333;
  
  @media (max-width: 968px) {
    font-size: clamp(1.2rem, 2.5vw, 1.4rem);
    line-height: clamp(1.6, 2.5vw, 1.8);
  }
  
  @media (max-width: 480px) {
    font-size: clamp(1.3rem, 3vw, 1.5rem);
    line-height: clamp(1.7, 3vw, 1.9);
  }
`;

export const NewsletterText = styled.p<{ fontWeight?: number }>`
  font-family: 'Poppins', sans-serif;
  font-size: clamp(0.7rem, 1.2vw, 1rem);
  line-height: clamp(1.3, 1.8vw, 1.5);
  color: white;
  margin-bottom: clamp(15px, 2vw, 25px);
  font-weight: ${props => props.fontWeight || 400};
  
  @media (max-width: 968px) {
    font-size: clamp(1.3rem, 3vw, 1.6rem);
    line-height: clamp(1.8, 3vw, 2.1);
  }
  
  @media (max-width: 480px) {
    font-size: clamp(1.4rem, 4vw, 1.7rem);
    line-height: clamp(1.9, 4vw, 2.2);
  }
`;

export const NewsletterButtonWrapper = styled.div`
  margin-top: clamp(30px, 4vw, 40px);
  margin-bottom: clamp(20px, 3vw, 30px);
  display: flex;
  justify-content: center;
`;

export const NewsletterLogoContainer = styled(motion.div)`
  margin-top: clamp(50px, 6vw, 80px);
  text-align: center;
  
  img {
    height: clamp(60px, 8vw, 100px);
    width: auto;
    opacity: 0.9;
  }
`;

